import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { useCart } from '@/features/cart/CartContext';
import { track } from '@/client/lib/analytics';
import { LoadingState } from '@/components/States';
import { useI18n } from '@/client/i18n/I18nContext';
import { lp } from '@/client/i18n/locale';

type View = 'verifying' | 'paid' | 'pending' | 'failed' | 'error';

export function CheckoutReturnPage() {
  const { t } = useI18n();
  useMeta({ title: t('meta.checkoutReturn.title'), path: '/checkout/return', noindex: true });
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [view, setView] = useState<View>('verifying');
  const orderNumber = params.get('order') ?? sessionStorage.getItem('caro.lastOrder') ?? '';

  useEffect(() => {
    if (!orderNumber) {
      setView('error');
      return;
    }
    // Authoritative: verify server-side (never trust the redirect alone).
    api
      .verifyCheckout(orderNumber)
      .then((res) => {
        if (res.paymentStatus === 'paid') {
          clear();
          track('purchase', { orderNumber });
          setView('paid');
        } else if (res.paymentStatus === 'failed') {
          setView('failed');
        } else {
          setView('pending');
        }
      })
      .catch(() => setView('error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  if (view === 'verifying') return <LoadingState label={t('return.verifying')} />;

  return (
    <div className="container">
      <div className="state">
        {view === 'paid' && (
          <>
            <p className="eyebrow">{t('return.thanks')}</p>
            <h1 className="section-title">{t('return.paidTitle')}</h1>
            <p>
              {t('return.paidBody1')} <strong>{orderNumber}</strong>. {t('return.paidBody2')}
            </p>
            <div className="mt-6">
              <Link className="btn" to={lp('/')}>{t('common.backHome')}</Link>
            </div>
          </>
        )}
        {view === 'pending' && (
          <>
            <h1 className="section-title">{t('return.pendingTitle')}</h1>
            <p>
              {/* pendingBody2 starts with its own separator (French spaces it: ' ;'). */}
              {t('return.pendingBody1')} <strong>{orderNumber}</strong>{t('return.pendingBody2')}
            </p>
          </>
        )}
        {view === 'failed' && (
          <>
            <h1 className="section-title">{t('return.failedTitle')}</h1>
            <p>{t('return.failedBody')}</p>
            <div className="mt-6">
              <Link className="btn" to={lp('/checkout')}>{t('common.retry')}</Link>
            </div>
          </>
        )}
        {view === 'error' && (
          <>
            <h1 className="section-title">{t('return.errorTitle')}</h1>
            <p>{t('return.errorBody')}</p>
            <div className="mt-6">
              <Link className="btn btn--ghost" to={lp('/contacto')}>{t('return.contact')}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
